import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { CartProvider } from "@/contexts/CartContext";
import { AuthProvider } from "@/contexts/AuthContext";
import { PreferencesProvider } from "@/contexts/PreferencesContext";
import { SiteMetaHead } from "@/components/SiteMetaHead";
import { PageTracker } from "@/components/PageTracker";

// Eagerly load the homepage for fastest FCP
import Index from "./pages/Index";

// Lazy load all other pages
const Auth = lazy(() => import("./pages/Auth"));
const Shop = lazy(() => import("./pages/Shop"));
const ProductDetail = lazy(() => import("./pages/ProductDetail"));
const Cart = lazy(() => import("./pages/Cart"));
const Checkout = lazy(() => import("./pages/Checkout"));
const OrderConfirmation = lazy(() => import("./pages/OrderConfirmation"));
const Services = lazy(() => import("./pages/Services"));
const B2B = lazy(() => import("./pages/B2B"));
const About = lazy(() => import("./pages/About"));
const Contact = lazy(() => import("./pages/Contact"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Encyclopedia = lazy(() => import("./pages/Encyclopedia"));
const FAQ = lazy(() => import("./pages/FAQ"));
const ShippingInfo = lazy(() => import("./pages/ShippingInfo"));
const ReturnsPolicy = lazy(() => import("./pages/ReturnsPolicy"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));

// Admin Pages - lazy loaded
const Dashboard = lazy(() => import("./pages/admin/Dashboard"));
const Products = lazy(() => import("./pages/admin/Products"));
const Orders = lazy(() => import("./pages/admin/Orders"));
const Categories = lazy(() => import("./pages/admin/Categories"));
const Quotations = lazy(() => import("./pages/admin/Quotations"));
const ServiceBookings = lazy(() => import("./pages/admin/ServiceBookings"));
const AdminEncyclopedia = lazy(() => import("./pages/admin/Encyclopedia"));
const SiteSettings = lazy(() => import("./pages/admin/SiteSettings"));
const Sliders = lazy(() => import("./pages/admin/Sliders"));
const FishTypes = lazy(() => import("./pages/admin/FishTypes"));
const HomepageCategories = lazy(() => import("./pages/admin/HomepageCategories"));
const CurrencyRates = lazy(() => import("./pages/admin/CurrencyRates"));
const AboutPage = lazy(() => import("./pages/admin/AboutPage"));
const ShippingRates = lazy(() => import("./pages/admin/ShippingRates"));
const ContentPages = lazy(() => import("./pages/admin/ContentPages"));
const StockManagement = lazy(() => import("./pages/admin/StockManagement"));
const UserManagement = lazy(() => import("./pages/admin/UserManagement"));
const AuditLog = lazy(() => import("./pages/admin/AuditLog"));
const Analytics = lazy(() => import("./pages/admin/Analytics"));
const BackupRestore = lazy(() => import("./pages/admin/BackupRestore"));

// Lazy load non-critical UI components
const CurrencyConverter = lazy(() => import("@/components/CurrencyConverter").then(m => ({ default: m.CurrencyConverter })));
const LanguageTranslator = lazy(() => import("@/components/LanguageTranslator").then(m => ({ default: m.LanguageTranslator })));
const WelcomePreferencesModal = lazy(() => import("@/components/WelcomePreferencesModal").then(m => ({ default: m.WelcomePreferencesModal })));

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <CartProvider>
        <PreferencesProvider>
          <TooltipProvider>
            <SiteMetaHead />
            <Toaster />
            <Sonner />
            <BrowserRouter>
              <PageTracker />
              <Suspense fallback={null}>
                <WelcomePreferencesModal />
                <CurrencyConverter />
                <LanguageTranslator />
              </Suspense>
              <Suspense fallback={<div className="min-h-screen" />}>
                <Routes>
                  <Route path="/" element={<Index />} />
                  <Route path="/auth" element={<Auth />} />
                  <Route path="/shop" element={<Shop />} />
                  <Route path="/product/:slug" element={<ProductDetail />} />
                  <Route path="/cart" element={<Cart />} />
                  <Route path="/checkout" element={<Checkout />} />
                  <Route path="/order-confirmation/:orderId" element={<OrderConfirmation />} />
                  <Route path="/services" element={<Services />} />
                  <Route path="/b2b" element={<B2B />} />
                  <Route path="/about" element={<About />} />
                  <Route path="/contact" element={<Contact />} />
                  <Route path="/faq" element={<FAQ />} />
                  <Route path="/shipping" element={<ShippingInfo />} />
                  <Route path="/returns" element={<ReturnsPolicy />} />
                  <Route path="/privacy" element={<PrivacyPolicy />} />
                  <Route path="/terms" element={<TermsOfService />} />
                  
                  {/* You Know Encyclopedia */}
                  <Route path="/you-know" element={<Encyclopedia />} />
                  <Route path="/you-know/:slug" element={<Encyclopedia />} />
                  
                  {/* Admin Routes */}
                  <Route path="/admin" element={<Dashboard />} />
                  <Route path="/admin/products" element={<Products />} />
                  <Route path="/admin/orders" element={<Orders />} />
                  <Route path="/admin/categories" element={<Categories />} />
                  <Route path="/admin/quotations" element={<Quotations />} />
                  <Route path="/admin/services" element={<ServiceBookings />} />
                  <Route path="/admin/encyclopedia" element={<AdminEncyclopedia />} />
                  <Route path="/admin/site-settings" element={<SiteSettings />} />
                  <Route path="/admin/sliders" element={<Sliders />} />
                  <Route path="/admin/fish-types" element={<FishTypes />} />
                  <Route path="/admin/homepage-categories" element={<HomepageCategories />} />
                  <Route path="/admin/currency-rates" element={<CurrencyRates />} />
                  <Route path="/admin/about-page" element={<AboutPage />} />
                  <Route path="/admin/shipping-rates" element={<ShippingRates />} />
                  <Route path="/admin/content-pages" element={<ContentPages />} />
                  <Route path="/admin/stock" element={<StockManagement />} />
                  <Route path="/admin/users" element={<UserManagement />} />
                  <Route path="/admin/audit" element={<AuditLog />} />
                  <Route path="/admin/analytics" element={<Analytics />} />
                  <Route path="/admin/backup" element={<BackupRestore />} />
                  
                  {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
            </BrowserRouter>
          </TooltipProvider>
        </PreferencesProvider>
      </CartProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;